import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpenCheck, GraduationCap, FileText, Send, Mail, Stamp, Clock, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../i18n/LangContext";
import boardingImg from "../assets/sections/boarding-school.png";

const svc = [
  { id: "bachelor",    icon: GraduationCap, price: 2200, duration: "3–4 yrs", img: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=900" },
  { id: "master",      icon: BookOpenCheck, price: 2200, duration: "1–2 yrs", img: "https://images.pexels.com/photos/6147166/pexels-photo-6147166.jpeg?w=900" },
  { id: "foundation",  icon: FileText,       price: 2200, duration: "1 year",  img: "https://images.unsplash.com/photo-1583373834259-46cc92173cb7?w=900" },
  { id: "application", icon: Send,           price: 1000, duration: "4–8 wks", img: "https://images.unsplash.com/photo-1584003564911-a7a321c84e1c?w=900" },
  { id: "motivation",  icon: Mail,           price: 400,  duration: "1–2 wks", img: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=900" },
  { id: "visa",        icon: Stamp,          price: 350,  duration: "2–6 wks", img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=900" }
];

const ServiceCard = ({ s, idx, onOpenForm, t }) => {
  const Icon = s.icon;
  const data = t(`services.items.${s.id}`);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const c = cardRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = ((y / r.height) - 0.5) * -8;  // tilt up/down
    const ry = ((x / r.width) - 0.5) * 10;   // tilt left/right
    c.style.setProperty("--mx", `${x}px`);
    c.style.setProperty("--my", `${y}px`);
    c.style.transform = `perspective(1200px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
  };

  const handleMouseLeave = () => {
    const c = cardRef.current;
    if (c) c.style.transform = "";
  };

  return (
    <button
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onOpenForm}
      className="service-card group relative rounded-2xl p-5 lg:p-4 text-left overflow-hidden transition-transform duration-500 ease-out"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", transformStyle: 'preserve-3d', willChange: 'transform' }}
    >
      {/* Background photo (fades in on hover) */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-700"
        style={{ backgroundImage: `url(${s.img})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'grayscale(80%) contrast(0.9)' }}
      />
      {/* Dark overlay above image for legibility */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'linear-gradient(135deg, rgba(27,42,74,0.85) 0%, rgba(27,42,74,0.55) 100%)' }} />
      {/* Spotlight follow-cursor */}
      <div className="spotlight pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-all duration-500 group-hover:rotate-6 group-hover:scale-110" style={{ background: 'rgba(201,169,110,0.16)', color: '#DBC097' }}>
          <Icon className="w-4 h-4" />
        </div>

        {/* Title + subtitle */}
        <h3 className="text-white font-display text-base lg:text-sm xl:text-base uppercase leading-tight">{data.title}</h3>
        <div className="text-[9px] text-white/45 mt-1 mb-3 uppercase tracking-[0.22em] flex items-center gap-2 flex-wrap">
          <span>{data.subtitle}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration}</span>
        </div>

        {/* Description */}
        <p className="text-xs text-white/65 leading-relaxed">{data.desc}</p>

        {/* Features */}
        <ul className="mt-4 space-y-1.5">
          {data.features.map((f, i) => (
            <li
              key={i}
              className="text-[11px] text-white/70 flex items-start gap-2 transition-all duration-500"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="w-3 h-px mt-2 flex-shrink-0 transition-all duration-500 group-hover:w-5" style={{ background: '#C9A96E' }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* CTA row */}
        <div className="mt-5 flex items-center justify-between pt-4 border-t border-white/10 gap-2">
          <span className="text-[9px] uppercase tracking-[0.22em] font-bold" style={{ color: '#DBC097' }}>
            {t("cta.leaveRequest")}
          </span>
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:rotate-45 group-hover:scale-110" style={{ background: '#C9A96E', color: '#1B2A4A' }}>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </button>
  );
};

const Services = ({ onOpenForm }) => {
  const { t } = useLang();
  return (
    <>
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: '#1B2A4A' }}>
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full blur-3xl" style={{ background: 'rgba(201,169,110,0.10)' }} />
        <div className="absolute -bottom-40 -left-20 w-[600px] h-[600px] rounded-full blur-3xl" style={{ background: 'rgba(201,169,110,0.07)' }} />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-3" style={{ color: '#C9A96E' }}>{t("services.kicker")}</div>
              <h2 className="headline-huge text-3xl md:text-5xl text-white text-balance max-w-2xl">{t("services.title")}</h2>
            </div>
            <p className="text-white/65 max-w-md">{t("services.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {svc.map((s, idx) => (
              <ServiceCard key={s.id} s={s} idx={idx} onOpenForm={onOpenForm} t={t} />
            ))}
          </div>

          {/* Strategy CTA */}
          <div className="mt-12 md:mt-16 relative rounded-3xl overflow-hidden p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10" style={{ background: 'linear-gradient(135deg, rgba(201,169,110,0.16) 0%, rgba(201,169,110,0.06) 100%)', border: '1px solid rgba(201,169,110,0.35)' }} data-testid="strategy-cta-band">
            <div className="w-14 h-14 rounded-2xl flex-shrink-0 flex items-center justify-center" style={{ background: '#C9A96E', color: '#1B2A4A' }}>
              <ClipboardList className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#DBC097' }}>Стратегія вступу · Ексклюзивна послуга</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase text-white leading-tight">Замовити персональну стратегію вступу</h3>
              <p className="text-white/70 mt-3 max-w-2xl text-sm md:text-base leading-relaxed">Заповніть анкету з 8 кроків — і протягом 48 годин ви отримаєте персональну стратегію: список університетів, чіткий план, ключові дедлайни та рекомендації від команди STUDYGRI.</p>
            </div>
            <Link to="/strategy-form" className="inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold whitespace-nowrap transition hover:scale-105" style={{ background: '#C9A96E', color: '#1B2A4A' }} data-testid="strategy-cta-btn">
              Заповнити анкету <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section id="schools" className="py-20 md:py-28 section-soft">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="relative rounded-3xl overflow-hidden shadow-xl" style={{ border: '1px solid rgba(201,169,110,0.3)' }}>
            <img src={boardingImg} alt="STUDYGRI Academy boarding school" className="w-full h-[420px] md:h-[520px] object-cover" />
            <div className="absolute bottom-5 left-5 right-5 glass-dark rounded-2xl p-4 text-white">
              <div className="text-xs uppercase tracking-widest" style={{ color: '#DBC097' }}>{t("boarding.premium")}</div>
              <div className="font-display text-2xl uppercase mt-1">{t("boarding.premiumDesc")}</div>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">{t("boarding.kicker")}</div>
            <h2 className="headline-huge text-3xl md:text-5xl text-balance">{t("boarding.title")}</h2>
            <p className="mt-4 text-slate-600">{t("boarding.subtitle")}</p>
            <ul className="mt-6 space-y-3">
              {t("boarding.features").map((f, i) => (<li key={i} className="flex items-center gap-3 text-slate-800"><span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: '#f3e8d6', color: '#A88D54' }}>{i + 1}</span> {f}</li>))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Button onClick={onOpenForm} className="btn-amber rounded-full h-12 px-7 font-semibold">{t("cta.leaveRequest")}</Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
